import {
  acceptMakeupChange,
  approveMakeupSession,
  cancelMakeupSession,
  rejectMakeupChange,
  rejectMakeupSession,
  requestMakeupChange,
} from "@/lib/services/makeupService";
import type {
  MakeupCancellationRequest,
  MakeupChangeRequest,
  RejectMakeupChangeRequest,
  RejectMakeupRequest,
} from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import {
  MakeupReviewConflictError,
  parseMakeupReviewConflictFromError,
} from "./makeup-review-conflict";

async function runMakeupAction(action: () => Promise<void>): Promise<void> {
  try {
    await action();
  } catch (error) {
    const conflict = await parseMakeupReviewConflictFromError(error);
    if (conflict) {
      throw new MakeupReviewConflictError(conflict);
    }
    throw error;
  }
}

export async function approveMakeupSessionAction(
  accessToken: string,
  id: string,
): Promise<void> {
  await runMakeupAction(() => approveMakeupSession(accessToken, id));
}

export async function rejectMakeupSessionAction(
  accessToken: string,
  id: string,
  body: RejectMakeupRequest,
): Promise<void> {
  await runMakeupAction(() => rejectMakeupSession(accessToken, id, body));
}

export async function requestMakeupChangeAction(
  accessToken: string,
  id: string,
  body: MakeupChangeRequest,
): Promise<void> {
  await runMakeupAction(() => requestMakeupChange(accessToken, id, body));
}

export async function acceptMakeupChangeAction(
  accessToken: string,
  id: string,
): Promise<void> {
  await runMakeupAction(() => acceptMakeupChange(accessToken, id));
}

export async function rejectMakeupChangeAction(
  accessToken: string,
  id: string,
  body: RejectMakeupChangeRequest,
): Promise<void> {
  await runMakeupAction(() => rejectMakeupChange(accessToken, id, body));
}

export async function cancelMakeupSessionAction(
  accessToken: string,
  id: string,
  body: MakeupCancellationRequest,
): Promise<void> {
  await runMakeupAction(() => cancelMakeupSession(accessToken, id, body));
}

/** Non-throwing probe — true when API returns 409 for stale pending. */
export async function isMakeupReviewConflictError(
  error: unknown,
): Promise<boolean> {
  if (error instanceof MakeupReviewConflictError) return true;
  if (error instanceof ResponseError && error.response.status === 409) {
    return true;
  }
  return false;
}
